<template>
  <v-layout wrap service-selector service-selection-component>
    <v-flex
      xs12
      vpx-1
      :class="displayClasses"
      data-testid="os-scheduler-services-section"
    >
      <h3
        v-if="showServicesHeading"
        class="column-heading"
        data-testid="os-scheduler-services-heading"
      >
        <i class="fa fa-file-o" />
        SERVICES
      </h3>
      <div data-testid="os-scheduler-services-select">
        <v-select
          ref="serviceSelect"
          v-model="serviceIds"
          :items="services"
          :menu-props="menuProps"
          item-text="attributes.name"
          item-value="attributes.id"
          data-cy="inspection_service_select"
          placeholder="Select Services"
          @input="toggleSelection"
          :attach="true"
          multiple
          id="service-selection-dropdown"
          v-bind="errorAttrs('service', 'service-selection-dropdown')"
        >
          <template #message="{ message }">
            <span
              :id="errorId('service-selection-dropdown')"
              role="alert"
              :data-testid="errorTestid('service')"
              >{{ message }}</span
            >
          </template>
          <template slot="item" slot-scope="data">
            <v-flex
              xs12
              class="service-selection"
              data-testid="os-scheduler-service-option"
              :id="`service-item__${data.item.index}`"
            >
              <v-flex xs2 md1>
                <v-checkbox
                  v-model="data.item.selected"
                  :id="`service-checkbox__${data.item.attributes.name.replace(
                    /[^a-zA-Z0-9]/g,
                    ''
                  )}`"
                />
              </v-flex>
              <v-layout wrap column class="d-flex name name-and-description">
                <v-flex
                  class="pay-at-close-badge"
                  v-if="showPayAtCloseBadge(data.item)"
                >
                  Pay at Close Option
                </v-flex>
                <v-flex xs12>
                  {{ data.item.attributes.name }}
                </v-flex>
                <div class="light small">{{
                  data.item.attributes.description
                }}</div>
              </v-layout>
            </v-flex>
          </template>
        </v-select>
      </div>
    </v-flex>
    <v-flex
      xs12
      vpx-1
      :class="displayClasses"
      data-testid="os-scheduler-addon-section"
    >
      <h3 class="column-heading" data-testid="os-scheduler-addon-heading">
        <i class="fa fa-plus" />
        ADD-ON SERVICES
      </h3>
      <v-flex
        xs12
        v-for="serviceId in selectedServicesWithAddOns"
        :key="serviceId"
        class="addon-select"
        :class="{ 'addon-select--ringed': showRing }"
        data-testid="os-scheduler-addon-select"
      >
        <v-select
          v-model="addOnIds"
          :items="addOnsForService[serviceId]"
          :menu-props="menuProps"
          item-text="attributes.name"
          item-value="attributes.id"
          placeholder="Select Add Ons"
          @input="toggleAddOnSelection"
          :label="addOnLabel(serviceId)"
          :attach="true"
          multiple
        >
          <template slot="item" slot-scope="data">
            <v-flex
              xs12
              class="service-selection"
              data-testid="os-scheduler-addon-option"
            >
              <v-flex xs1>
                <v-checkbox
                  v-model="data.item.selected"
                  :id="`service-addon-checkbox__${data.item.attributes.name.replace(
                    /[^a-zA-Z0-9]/g,
                    ''
                  )}`"
                />
              </v-flex>
              <v-layout wrap xs11 class="name name-and-description">
                <div>{{ data.item.attributes.name }}</div>
                <div class="light small">{{
                  data.item.attributes.description
                }}</div>
              </v-layout>
            </v-flex>
          </template>
        </v-select>
      </v-flex>
      <v-flex
        xs12
        v-if="!selectedServicesWithAddOns.length"
        data-testid="os-scheduler-addon-placeholder"
      >
        <v-select
          :items="EMPTY"
          :value="EMPTY"
          placeholder="Select Add Ons"
          :hint="addOnGhostText"
          persistent-hint
          disabled
          multiple
        />
      </v-flex>
    </v-flex>
  </v-layout>
</template>

<script>
import fieldErrors from '@/mixins/fieldErrors'
import { mapState } from 'vuex'

// Fixed copy, not state-dependent — a `const` says so at a glance where a computed
// would imply the sentence varies. Rendered as a persistent Vuetify hint rather than
// the input's placeholder: a ~90-character placeholder on a single-line <input> is
// clipped at phone widths and cannot wrap.
const ADD_ON_GHOST_TEXT =
  'Add-on services are available for specific primary services. Please choose a Service first.'

// Shared frozen empty array for the disabled ghost field's `:items`/`:value`.
// Vuetify 1.5's VSelect watches `items` with `immediate: true` and VTextField watches
// `value`, and both fire on identity change rather than content change — so inline `[]`
// literals would re-run `setSelectedItems()` on every parent re-render (every service
// toggle, every Vuex addOns/services update) for a permanently-empty, disabled field.
const EMPTY = Object.freeze([])

export default {
  mixins: [fieldErrors],
  computed: {
    ...mapState('inspection', [
      'addOns',
      'agreements',
      'company',
      'services',
      'templates',
    ]),
    addOnsForService() {
      return this.addOns.reduce((summ, ao) => {
        if (summ[ao.attributes.service_id]) {
          summ[ao.attributes.service_id].push(ao)
        } else {
          summ[ao.attributes.service_id] = [ao]
        }
        return summ
      }, {})
    },
    defaultMenuProps() {
      return { maxHeight: '400' }
    },
    displayClasses() {
      return this.display === 'rows' ? '' : 'sm6 md4'
    },
    // The primary services the client has chosen that actually offer add-ons. Drives
    // both branches of the Add-On section: non-empty renders one select per service,
    // empty renders the disabled ghost-text field.
    selectedServicesWithAddOns() {
      return this.serviceIds.filter((serviceId) => this.hasAddOns[serviceId])
    },
    hasAddOns() {
      return this.addOns.reduce((summ, ao) => {
        summ[ao.attributes.service_id] = true
        return summ
      }, {})
    },
    canUsePayAtClose() {
      return this.company.attributes.pay_at_close_eligibility
        .can_use_pay_at_close
    },
  },
  data() {
    let info = {
      addOnIds: [],
      serviceIds: [],
      showRing: false,
      addOnGhostText: ADD_ON_GHOST_TEXT,
      EMPTY,
    }

    if (this.initialServiceIds) {
      info.serviceIds = [...this.initialServiceIds]
    }

    if (this.initialAddOnsIds) {
      info.addOnIds = [...this.initialAddOnsIds]
    }

    return info
  },
  props: {
    display: { default: 'rows' },
    initialAddOnsIds: {
      type: Array,
      default: () => [],
    },
    loadResources: { default: false },
    menuProps: {
      function() {
        return { maxHeight: '400' }
      },
    },
    initialServiceIds: {
      type: Array,
      default: () => [],
    },
    enablePayAtCloseBadge: {
      type: Boolean,
      default: false,
    },
    // Puts the SERVICES heading inside the services column, so it sits level with the
    // ADD-ON SERVICES heading once `display="columns"` lays the two out side by side.
    // A heading rendered by the parent above this component makes the add-on column one
    // heading taller and pushes it a row down. The internal forms stack their columns
    // and render their own heading outside, so they leave this off.
    showServicesHeading: {
      type: Boolean,
      default: false,
    },
    // Opt-in, and deliberately default-off: the ring is defined against the Online
    // Scheduler's stepper ("show on menu close, clear on returning to step 1"), and the
    // internal New Inspection / Quote forms have no steps, so an unclearable green
    // highlight there would mean nothing. Only SelectService.vue passes this.
    highlightAddOns: {
      type: Boolean,
      default: false,
    },
    // Whether the scheduler step hosting this component is the visible one. Supplied by
    // SelectService.vue, absent (false) everywhere else. See the `active` watcher.
    active: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    addOnLabel(serviceId) {
      let name = this.services.find((s) => s.attributes.id === serviceId)
        .attributes.name
      return `${name} Options`
    },
    setupPage() {
      this.$store.dispatch('inspection/fetchServices')
      this.$store.dispatch('inspection/fetchTemplates')
      this.$store.dispatch('inspection/fetchAgreements')
    },
    toggleAddOnSelection(e) {
      this.addOns.forEach((ao) => {
        ao.selected = e.includes(ao.attributes.id)
      })

      this.$emit('addonChange', e)
    },
    toggleSelection(e) {
      let emitAddons = false

      this.services.forEach((s) => {
        s.selected = e.includes(s.attributes.id)
        if (!s.selected) {
          this.addOns.forEach((ao) => {
            if (ao.attributes.service_id === s.attributes.id) {
              ao.selected = false
              this.addOnIds = this.addOnIds.filter(
                (aoId) => aoId !== ao.attributes.id
              )
              emitAddons = true
            }
          })
        }
      })

      if (emitAddons) this.$emit('addonChange', this.addOnIds)
      this.$emit('serviceChange', e)
    },

    // Raise the ring when the Services menu closes with at least one add-on-bearing
    // service chosen, so the client's eye is pulled to the Add-On field they could not
    // see while the menu was over it.
    //
    // Vuetify 1.5's v-select emits no `update:menu`, so this watches the select's own
    // `isMenuActive`. Chosen over `@blur` because it fires for every close path a phone
    // user can take — Escape, tapping outside, tapping the overlay — including the ones
    // that leave focus in place.
    //
    // The `highlightAddOns` gate is the FIRST thing here, not a guard inside the
    // callback, so the internal New Inspection / Quote forms never install a watcher
    // reaching into Vuetify 1.5 private state at all — HARD CONSTRAINT 2 by
    // construction rather than by a late return.
    //
    // The watcher is installed on the child instance (`select.$watch('isMenuActive')`)
    // rather than on a parent getter that reads through `$refs`. `$refs` is not
    // reactive, so a parent getter evaluated while the ref is still empty would collect
    // no dependency and stay permanently inert, with no re-arm and no error. Watching
    // the child directly cannot short-circuit that way, and a missing ref is reported
    // at install time instead of leaving a watcher that silently never fires.
    watchServicesMenu() {
      if (!this.highlightAddOns) return

      const select = this.$refs.serviceSelect
      if (!select) {
        // Reported rather than thrown: this runs first in `mounted()`, and throwing
        // would skip `setupPage()` and leave the public booking form with no services.
        // The ring is an enhancement; the form is the product.
        console.error(
          '[ServiceSelection] highlightAddOns is on but the Services v-select ref is ' +
            'missing, so the Add-On ring can never be raised. If this select is now ' +
            'rendered conditionally, install this watcher once it exists.'
        )
        return
      }

      select.$watch('isMenuActive', (isOpen, wasOpen) => {
        if (!wasOpen || isOpen) return
        if (!this.selectedServicesWithAddOns.length) return
        this.showRing = true
      })
    },

    showPayAtCloseBadge(service) {
      return (
        this.enablePayAtCloseBadge &&
        this.canUsePayAtClose &&
        service.attributes.enable_pay_at_close
      )
    },
  },
  mounted() {
    this.watchServicesMenu()
    if (this.loadResources) this.setupPage()
    if (this.draftServiceIds) {
      this.services.forEach((s) => {
        s.selected = this.draftServiceIds.includes(s.attributes.id)
      })
      this.serviceIds = this.draftServiceIds
    }

    if (this.draftAddOnsIds) {
      this.addOns.forEach((ao) => {
        if (this.draftAddOnsIds.includes(ao.attributes.id)) {
          ao.selected = true
        }
        this.addOnIds = this.draftAddOnsIds
      })
    }
  },
  watch: {
    initialServiceIds: {
      deep: true,
      handler: function (newV, oldV) {
        this.serviceIds = [...newV]
        this.services.forEach((service) => {
          service.selected = this.serviceIds.includes(service.attributes.id)
        })
      },
    },
    initialAddOnsIds: {
      deep: true,
      handler: function (newV, oldV) {
        this.addOnIds = [...newV]
        this.addOns.forEach((addOn) => {
          addOn.selected = this.addOnIds.includes(addOn.attributes.id)
        })
      },
    },
    // VStepperContent renders its slot behind a `v-show` directive rather than a
    // conditional, so step 1's component stays mounted while the client is on a later
    // step. Coming back therefore does NOT re-create this component, and a ring raised
    // before leaving would still be showing. Clear it on re-entry.
    active(isActive, wasActive) {
      if (isActive && !wasActive) this.showRing = false
    },
    // Nothing left to point at once the client deselects every service that had add-ons.
    selectedServicesWithAddOns(withAddOns) {
      if (!withAddOns.length) this.showRing = false
    },
  },
}
</script>

<style scoped lang="scss">
@import '../../../assets/stylesheets/variables.scss';

.service-selection {
  font-size: 1em;
  color: #2b6088;
  display: flex;
  align-items: center;
}

.service-selection div {
  display: inline-block;
}

.service-selector .service-selection .name {
  margin-left: 0.5em;
}
.service-selector .service-selection .name-and-description {
  padding: 0.5em 0;
}

/* Shared by both column headings: side by side, any difference in their box height
   offsets one column's select from the other's. */
.column-heading {
  margin-bottom: 0.25em;
}

/* The transparent border is always present so raising the ring never reflows the
   field — only its colour changes. No transition: `style_hierarchy.md` ground rule 3
   defaults to no bespoke animation, the ring is legible without one, and animating it
   would hand a `toHaveCSS('border-color', …)` assertion a window of interpolated
   colour. */
.addon-select {
  border: 1px solid transparent;
  border-radius: 4px;
}

.addon-select--ringed {
  border-color: $highlight-green;
}
</style>

<style lang="scss">
.service-selection-component {
  .v-list__tile--link {
    height: auto;
    min-height: 48px;
  }

  .v-input--checkbox {
    margin-top: 5px;
  }
  .v-input--checkbox .v-input__slot {
    margin: 0;
  }
}

.pay-at-close-badge {
  color: #1771b8;
  font-size: 12px;
  font-weight: 500;
  display: inline-flex;
  padding: 2px 5px;
  align-items: center;
  align-self: self-start;
  gap: 5px;
  border-radius: 20px;
  border: 1px solid #1771b8;
  background-color: #f1f8fd;
}
</style>
